/* ============================================
   useWiki — Wiki Data Management Hook
   Categories, articles, CRUD, and search.
   Uses supabase.from() directly (documented exception)
   ============================================ */

import { useState, useCallback } from 'react'
import { supabase } from '@/lib/supabase'

/* ── Types ── */
export interface WikiCategoria {
  id: string
  nombre: string
  slug: string
  descripcion: string | null
  icono: string | null
  orden: number
  color: string | null
  parent_id: string | null
  created_at: string
  updated_at: string
}

export interface WikiArticulo {
  id: string
  categoria_id: string
  titulo: string
  slug: string
  contenido: string
  resumen: string | null
  icono: string | null
  orden: number
  estado: 'borrador' | 'publicado' | 'archivado'
  autor_id: string | null
  metadata: Record<string, any>
  created_at: string
  updated_at: string
  categoria?: WikiCategoria
}

export interface CategoriaConConteo extends WikiCategoria {
  articulos_count: number
  articulos: Pick<WikiArticulo, 'id' | 'titulo' | 'slug' | 'estado' | 'orden' | 'icono'>[]
}

export interface WikiArticuloInput {
  categoria_id: string
  titulo: string
  slug: string
  contenido: string
  resumen?: string | null
  icono?: string | null
  orden?: number
  estado?: 'borrador' | 'publicado' | 'archivado'
  autor_id?: string | null
  metadata?: Record<string, any>
}

export interface WikiCategoriaInput {
  nombre: string
  slug: string
  descripcion?: string | null
  icono?: string | null
  orden?: number
  color?: string | null
  parent_id?: string | null
}

export function useWiki() {
  const [categorias, setCategorias] = useState<CategoriaConConteo[]>([])
  const [articuloActual, setArticuloActual] = useState<WikiArticulo | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  /* ── Load categories with article count ── */
  const loadCategorias = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const { data: cats, error: catErr } = await (supabase
        .from('wiki_categorias') as any)
        .select('*')
        .order('orden', { ascending: true })

      if (catErr) throw catErr

      const { data: arts, error: artErr } = await (supabase
        .from('wiki_articulos') as any)
        .select('id, titulo, slug, estado, orden, icono, categoria_id')
        .order('orden', { ascending: true })

      if (artErr) throw artErr

      const categoriasConConteo: CategoriaConConteo[] = (cats || []).map((cat: WikiCategoria) => {
        const catArticulos = (arts || []).filter(
          (a: WikiArticulo) => a.categoria_id === cat.id
        )
        return {
          ...cat,
          articulos_count: catArticulos.length,
          articulos: catArticulos,
        }
      })

      setCategorias(categoriasConConteo)
    } catch (err: any) {
      console.error('Error loading wiki categorias:', err)
      setError(err.message || 'Error cargando categorías')
    }
    setLoading(false)
  }, [])

  /* ── Load article by slugs ── */
  const loadArticulo = useCallback(async (categoriaSlug: string, articuloSlug: string) => {
    setLoading(true)
    setError(null)
    try {
      // First find the category
      const { data: cat, error: catErr } = await (supabase
        .from('wiki_categorias') as any)
        .select('*')
        .eq('slug', categoriaSlug)
        .single()

      if (catErr) throw catErr
      if (!cat) throw new Error('Categoría no encontrada')

      // Then find the article
      const { data: art, error: artErr } = await (supabase
        .from('wiki_articulos') as any)
        .select('*')
        .eq('categoria_id', cat.id)
        .eq('slug', articuloSlug)
        .single()

      if (artErr) throw artErr
      if (!art) throw new Error('Artículo no encontrado')

      const articulo: WikiArticulo = { ...art, categoria: cat }
      setArticuloActual(articulo)
      return articulo
    } catch (err: any) {
      console.error('Error loading wiki articulo:', err)
      setError(err.message || 'Error cargando artículo')
      setArticuloActual(null)
      return null
    } finally {
      setLoading(false)
    }
  }, [])

  /* ── Search articles ── */
  const buscar = useCallback(async (query: string): Promise<WikiArticulo[]> => {
    if (!query.trim()) return []
    try {
      const { data, error: searchErr } = await (supabase
        .from('wiki_articulos') as any)
        .select('*, categoria:wiki_categorias(*)')
        .or(`titulo.ilike.%${query}%,contenido.ilike.%${query}%`)
        .order('orden', { ascending: true })
        .limit(20)

      if (searchErr) throw searchErr
      return (data || []) as WikiArticulo[]
    } catch (err: any) {
      console.error('Error searching wiki:', err)
      return []
    }
  }, [])

  /* ── Create article ── */
  const crearArticulo = useCallback(async (input: WikiArticuloInput): Promise<WikiArticulo | null> => {
    try {
      const { data, error: insertErr } = await (supabase
        .from('wiki_articulos') as any)
        .insert({
          ...input,
          metadata: input.metadata || {},
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        })
        .select('*')
        .single()

      if (insertErr) throw insertErr
      return data as WikiArticulo
    } catch (err: any) {
      console.error('Error creating wiki articulo:', err)
      setError(err.message || 'Error creando artículo')
      return null
    }
  }, [])

  /* ── Update article ── */
  const actualizarArticulo = useCallback(async (
    id: string,
    updates: Partial<WikiArticuloInput>
  ): Promise<WikiArticulo | null> => {
    try {
      const { data, error: updateErr } = await (supabase
        .from('wiki_articulos') as any)
        .update({
          ...updates,
          updated_at: new Date().toISOString(),
        })
        .eq('id', id)
        .select('*')
        .single()

      if (updateErr) throw updateErr
      return data as WikiArticulo
    } catch (err: any) {
      console.error('Error updating wiki articulo:', err)
      setError(err.message || 'Error actualizando artículo')
      return null
    }
  }, [])

  /* ── Delete article ── */
  const eliminarArticulo = useCallback(async (id: string): Promise<boolean> => {
    try {
      const { error: deleteErr } = await (supabase
        .from('wiki_articulos') as any)
        .delete()
        .eq('id', id)

      if (deleteErr) throw deleteErr
      setArticuloActual(null)
      return true
    } catch (err: any) {
      console.error('Error deleting wiki articulo:', err)
      setError(err.message || 'Error eliminando artículo')
      return false
    }
  }, [])

  /* ── Create category ── */
  const crearCategoria = useCallback(async (input: WikiCategoriaInput): Promise<WikiCategoria | null> => {
    try {
      const { data, error: insertErr } = await (supabase
        .from('wiki_categorias') as any)
        .insert({
          ...input,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        })
        .select('*')
        .single()

      if (insertErr) throw insertErr
      return data as WikiCategoria
    } catch (err: any) {
      console.error('Error creating wiki categoria:', err)
      setError(err.message || 'Error creando categoría')
      return null
    }
  }, [])

  /* ── Update category ── */
  const actualizarCategoria = useCallback(async (
    id: string,
    updates: Partial<WikiCategoriaInput>
  ): Promise<WikiCategoria | null> => {
    try {
      const { data, error: updateErr } = await (supabase
        .from('wiki_categorias') as any)
        .update({
          ...updates,
          updated_at: new Date().toISOString(),
        })
        .eq('id', id)
        .select('*')
        .single()

      if (updateErr) throw updateErr
      return data as WikiCategoria
    } catch (err: any) {
      console.error('Error updating wiki categoria:', err)
      setError(err.message || 'Error actualizando categoría')
      return null
    }
  }, [])

  return {
    categorias,
    articuloActual,
    loading,
    error,
    setArticuloActual,
    loadCategorias,
    loadArticulo,
    buscar,
    crearArticulo,
    actualizarArticulo,
    eliminarArticulo,
    crearCategoria,
    actualizarCategoria,
  }
}
